import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useEffect, useCallback, useMemo, useState } from 'react';
import { ChevronRight, Loader2, Search } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { useControlPanel } from '../../../contexts/ControlPanelContext';
import { useUserProjectsContext } from '../../../contexts/UserProjectsContext';
import { UserProjectItem } from '../UserProjectItem';
import { flyToUserProject, flyToUserProjectArea, updateUserProjectLabel, clearUserProjectData } from '../../../utils/cesiumHelpers';
import { pendingProjectUtils } from '../../../utils/pendingProject';
import { useEmailVerificationStatus } from '../../../hooks/useEmailVerificationStatus';
import { collaborationService } from '../../../services/projects/collaboration/collaborationService';
import { workspaceService } from '../../../services/workspaceService';
import { ContextMenu } from '../../ContextMenu/ContextMenu';
import { useContextMenu } from '../../ContextMenu/useContextMenu';
import { useProjectContextMenu } from '../../../hooks/useProjectContextMenu';
import { categorizeProject, presentCategories } from '../projectCategory';
import { FirstActionListCard } from '../../onboarding/FirstActionCard';
const MyProjectsSectionComponent = ({ isExpanded, onToggleSection, areaUnit, viewer, onSelectProject, onSwitchToProjectStructure, onEditProject, onRemoveProject }) => {
    const { isAuthenticated, user } = useAuth();
    const { actions: controlPanelActions } = useControlPanel();
    const { projects: userProjects, isLoading: userProjectsLoading, error: userProjectsError, refetch: refetchUserProjects, hasLoaded } = useUserProjectsContext();
    // Context menu for project items
    const contextMenu = useContextMenu();
    const { generateProjectMenuItems } = useProjectContextMenu({
        onEditProject,
        onRemoveProject
    });
    // Handle context menu for project items
    const handleShowContextMenu = useCallback((e, project) => {
        const menuItems = generateProjectMenuItems(project);
        contextMenu.showMenu({ x: e.clientX, y: e.clientY }, menuItems);
    }, [generateProjectMenuItems, contextMenu]);
    const emailVerificationPending = useEmailVerificationStatus(isAuthenticated);
    // Get pending project from localStorage if email verification is pending
    const pendingProject = useMemo(() => {
        if (emailVerificationPending) {
            const pending = pendingProjectUtils.retrieve();
            if (pending) {
                // Transform pending project to match user project structure
                return {
                    // Use a stable id derived from the stored timestamp to avoid React key churn
                    id: 'pending-' + pending.timestamp,
                    name: pending.projectName,
                    description: pending.projectDescription,
                    requestedAreaAcres: 0,
                    processingStatus: 'pending',
                    displayStatus: 'unpaid',
                    accessLevel: 'none',
                    requestedBounds: pending.coordinates.map(coord => ({ lat: coord.lat, lng: coord.lng })),
                    createdAt: Math.floor(pending.timestamp / 1000) // Convert to seconds
                };
            }
        }
        return null;
    }, [emailVerificationPending]);
    // Combine pending project with user projects for display. Public-demo copies
    // are owned projects too, but they belong under Demo Projects — filter them out.
    const allProjects = useMemo(() => {
        const projects = userProjects.filter((p) => !(p === null || p === void 0 ? void 0 : p.isDemo));
        if (pendingProject) {
            // Add pending project at the beginning
            projects.unshift(pendingProject);
        }
        return projects;
    }, [userProjects, pendingProject]);
    // Search + type filter state. With unlimited projects the list needs a way to
    // narrow by name/description and by the project's display type.
    const [searchQuery, setSearchQuery] = useState('');
    const [activeTypeFilters, setActiveTypeFilters] = useState(new Set());
    const toggleTypeFilter = useCallback((key) => {
        setActiveTypeFilters(prev => {
            const next = new Set(prev);
            if (next.has(key))
                next.delete(key);
            else
                next.add(key);
            return next;
        });
    }, []);
    // Only offer type chips for categories the user actually has.
    const availableCategories = useMemo(() => presentCategories(allProjects), [allProjects]);
    // Drop chips for types that no longer exist (e.g. a project changed state).
    useEffect(() => {
        setActiveTypeFilters(prev => {
            if (prev.size === 0)
                return prev;
            const present = new Set(availableCategories.map(c => c.key));
            let changed = false;
            const next = new Set();
            for (const k of prev) {
                if (present.has(k))
                    next.add(k);
                else
                    changed = true;
            }
            return changed ? next : prev;
        });
    }, [availableCategories]);
    const filteredProjects = useMemo(() => {
        const q = searchQuery.trim().toLowerCase();
        return allProjects.filter(project => {
            var _a, _b;
            if (activeTypeFilters.size > 0 && !activeTypeFilters.has(categorizeProject(project).key)) {
                return false;
            }
            if (q) {
                const haystack = `${(_a = project.name) !== null && _a !== void 0 ? _a : ''} ${(_b = project.description) !== null && _b !== void 0 ? _b : ''}`.toLowerCase();
                if (!haystack.includes(q))
                    return false;
            }
            return true;
        });
    }, [allProjects, searchQuery, activeTypeFilters]);
    // Load user projects when authenticated (and not unverified)
    useEffect(() => {
        if (isAuthenticated && (user === null || user === void 0 ? void 0 : user.loginMethod) !== 'unverified' && !hasLoaded && !userProjectsLoading) {
            refetchUserProjects();
        }
    }, [isAuthenticated, user === null || user === void 0 ? void 0 : user.loginMethod, hasLoaded, userProjectsLoading]); // Removed refetchUserProjects from deps
    // Update existing label when area unit changes
    useEffect(() => {
        updateUserProjectLabel(viewer, userProjects, areaUnit);
    }, [areaUnit, userProjects, viewer]);
    // Share filing: the recipient's workspaces for the "Show in" picker.
    // Fetched once when authenticated; the picker only renders with 2+ rooms
    // (with one workspace there is nothing to file between).
    const [myWorkspaces, setMyWorkspaces] = useState([]);
    useEffect(() => {
        if (!isAuthenticated)
            return;
        let cancelled = false;
        workspaceService.listMyWorkspaces()
            .then((ws) => { if (!cancelled)
            setMyWorkspaces(ws); })
            .catch(() => { });
        return () => { cancelled = true; };
    }, [isAuthenticated]);
    const handleFileShare = useCallback(async (projectId, homeAccountId) => {
        try {
            await collaborationService.setSharedProjectHome(projectId, homeAccountId);
            refetchUserProjects();
        }
        catch (e) {
            console.error('Failed to file shared project:', e);
        }
    }, [refetchUserProjects]);
    // Handle user project click - navigate to project or open structure view
    const handleUserProjectClick = useCallback(async (userProject) => {
        // Use displayStatus (server-computed) - backend is single source of truth
        if (userProject.displayStatus !== 'created') {
            try {
                // Remove placeholder polygon/label
                clearUserProjectData(viewer);
                // Fly camera without drawing placeholders
                await flyToUserProjectArea(viewer, userProject);
                // Switch control panel to structure mode first
                if (onSwitchToProjectStructure) {
                    onSwitchToProjectStructure();
                }
                // Create a project object that matches the Project interface expected by useProjects
                const projectObject = {
                    getId: () => userProject.id,
                    getName: () => userProject.name,
                    getBounds: () => {
                        if (userProject.requestedBounds && userProject.requestedBounds.length >= 3) {
                            const lats = userProject.requestedBounds.map((c) => c.lat);
                            const lngs = userProject.requestedBounds.map((c) => c.lng);
                            return {
                                getWest: () => Math.min(...lngs),
                                getSouth: () => Math.min(...lats),
                                getEast: () => Math.max(...lngs),
                                getNorth: () => Math.max(...lats)
                            };
                        }
                        return null;
                    },
                    getRequestedBoundsList: () => {
                        if (userProject.requestedBounds && userProject.requestedBounds.length >= 3) {
                            return userProject.requestedBounds.map((c) => ({
                                getLatitude: () => c.lat,
                                getLongitude: () => c.lng
                            }));
                        }
                        return [];
                    }
                };
                // Use the normal project selection flow
                if (onSelectProject) {
                    onSelectProject(projectObject);
                }
                return;
            }
            catch (e) {
                console.error('Failed to load user project structure:', e);
            }
        }
        // Fallback: projects without real data still show placeholder polygon/area label
        await flyToUserProject(viewer, userProject, areaUnit);
    }, [viewer, areaUnit, controlPanelActions, onSelectProject, onSwitchToProjectStructure]);
    // Don't render if not authenticated AND no pending email verification
    if (!isAuthenticated && !emailVerificationPending) {
        return null;
    }
    return (_jsxs("div", { className: "border-b flex-shrink-0", "data-tour": "my-projects", style: { borderColor: 'var(--pewter)' }, children: [_jsxs("div", { className: "p-4 flex items-center justify-between cursor-pointer hover:bg-opacity-80 transition-colors", onClick: () => onToggleSection('myProjects'), children: [_jsxs("div", { className: "flex items-center gap-2", children: [_jsx("div", { className: `transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`, children: _jsx(ChevronRight, { size: 14 }) }), _jsx("h2", { className: "text-sm font-semibold", style: { color: 'var(--sand)' }, children: "My Projects" })] }), _jsx("div", { className: "flex items-center gap-2", children: userProjectsLoading && userProjects.length === 0 && (_jsx(Loader2, { size: 16, className: "animate-spin", style: { color: 'var(--forest)' } })) })] }), isExpanded && (_jsxs("div", { className: "px-4 pb-4", children: [userProjectsError && (_jsxs("div", { className: "mt-3 p-3 rounded-md text-xs text-center", style: { backgroundColor: 'var(--charcoal)', color: 'var(--dew)' }, children: ["Error loading projects: ", userProjectsError] })), userProjectsLoading && userProjects.length === 0 && (_jsx("div", { className: "mt-3 p-3 rounded-md text-xs text-center", style: { backgroundColor: 'var(--charcoal)', color: 'var(--dew)' }, children: "Loading your projects..." })), !userProjectsLoading && allProjects.length === 0 && !userProjectsError && (_jsx(FirstActionListCard, { dismissedFallback: _jsx("div", { className: "mt-3 p-3 rounded-md text-xs text-center", style: { backgroundColor: 'var(--charcoal)', color: 'var(--dew)' }, children: "No projects found. Create your first project below!" }) })), allProjects.length > 0 && (_jsxs("div", { className: "mt-3 mb-2 space-y-2", children: [_jsxs("div", { className: "relative", children: [_jsx(Search, { size: 13, className: "absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none", style: { color: 'var(--forest)' } }), _jsx("input", { type: "text", value: searchQuery, onChange: (e) => setSearchQuery(e.target.value), placeholder: "Search projects...", className: "w-full pl-7 pr-2 py-1.5 text-xs rounded border focus:outline-none focus:ring-1", style: { backgroundColor: 'var(--black)', borderColor: 'var(--pewter)', color: 'var(--sand)' } })] }), availableCategories.length > 1 && (_jsxs("div", { className: "flex flex-wrap items-center gap-1.5", children: [_jsx("span", { className: "text-[10px] uppercase tracking-wider", style: { color: 'var(--dew)' }, children: "Filter" }), availableCategories.map((cat) => {
                                        const isOn = activeTypeFilters.has(cat.key);
                                        return (_jsx("button", { type: "button", onClick: () => toggleTypeFilter(cat.key), className: "inline-flex items-center px-2 py-0.5 rounded-full text-[11px] transition-opacity", style: {
                                                backgroundColor: isOn ? cat.color : 'transparent',
                                                color: isOn ? 'var(--sand)' : 'var(--dew)',
                                                border: `1px solid ${isOn ? cat.color : 'var(--pewter)'}`,
                                                opacity: isOn ? 1 : 0.85,
                                            }, children: cat.label }, cat.key));
                                    }), activeTypeFilters.size > 0 && (_jsx("button", { type: "button", onClick: () => setActiveTypeFilters(new Set()), className: "text-[10px] underline ml-1", style: { color: 'var(--dew)' }, children: "clear" }))] }))] })), allProjects.length > 0 && filteredProjects.length > 0 && (() => {
                        // Split the room's projects from true shares: a workspace seat sees
                        // the room's projects as its own; collaborator/client grants group
                        // under "Shared with you" (the whole list for an invited-only
                        // client viewer).
                        const isShare = (p) => p.isOwner === false && p.myRole !== 'member';
                        const ownProjects = filteredProjects.filter((p) => !isShare(p));
                        const sharedProjects = filteredProjects.filter(isShare);
                        return (_jsxs(_Fragment, { children: [_jsxs("div", { className: "space-y-1 max-h-64 overflow-y-auto", children: [ownProjects.map((userProject) => (_jsx(UserProjectItem, { userProject: userProject, areaUnit: areaUnit, onClick: handleUserProjectClick, onEditProject: onEditProject, onRemoveProject: onRemoveProject, onShowContextMenu: handleShowContextMenu }, userProject.id))), sharedProjects.length > 0 && (_jsxs("div", { className: ownProjects.length > 0 ? 'pt-2' : '', children: [_jsx("div", { className: "text-[10px] uppercase tracking-wider mb-1", style: { color: 'var(--dew)' }, children: "Shared with you" }), _jsx("div", { className: "space-y-1", children: sharedProjects.map((userProject) => (_jsx(UserProjectItem, { userProject: userProject, areaUnit: areaUnit, onClick: handleUserProjectClick, onEditProject: onEditProject, onRemoveProject: onRemoveProject, onShowContextMenu: handleShowContextMenu, shareWorkspaces: myWorkspaces, onFileShare: handleFileShare }, userProject.id))) })] }))] }), _jsx(ContextMenu, { items: contextMenu.menuItems, position: contextMenu.position, onClose: contextMenu.hideMenu, isOpen: contextMenu.isOpen })] }));
                    })(), allProjects.length > 0 && filteredProjects.length === 0 && (_jsx("div", { className: "mt-3 p-3 rounded-md text-xs text-center", style: { backgroundColor: 'var(--charcoal)', color: 'var(--dew)' }, children: "No projects match your search." }))] }))] }));
};
// Export component directly - React.memo was preventing re-renders when useUserProjects data changed
export const MyProjectsSection = MyProjectsSectionComponent;
